<template>
  <section>
    <h2 class="header">
      <span class="header__number">1</span>Choose Your Roll Off Dumpster
    </h2>
    <form class="zip-search" @submit.prevent>
      <fieldset class="fieldset">
        <h2 class="legend">1. Where do you need the dumpster?</h2>
        <div class="label label__wrapper mb-0">
          <img class="input__icon--left" :src="pinIcon" alt="map pin icon">
          <input
            autocomplete="off"
            ref="autocompleteField"
            id="address-search"
            name="search"
            :class="[
              {
                'autocomplete--active': autoCompleteIsActive,
                'input__with-error': showAddressError
             },
              'input input__with-icon--left input__without-label'
            ]"
            v-model="address"
            type="text"
            @change="addressChange"
            @blur="addressBlur"
            @focus="addressFocus"
            placeholder="Enter Project Address">
        </div>

        <dismissable :isWarning="true" :showClose="false" v-if="showAddressError">
          <template v-slot:content>
            <p>Please choose a valid address from the drop-down. Don't see your address?
              Reach out by phone at <a :href="`tel:${phoneNumber}`">{{ phoneNumber }}</a> or LiveChat.</p>
          </template>
        </dismissable>

        <!-- Project Types -->
        <div :class="{ 'opacity-20': !showProjectTypeTiles }">
          <h2 class="legend mt-2">2. What do you need the dumpster for?</h2>
          <div class="projects mb-2">
            <template v-for="p in projectTypes" :key="p.label">
              <label @change="runProjectMatrix(p.value)"
                :class="['projects__label', { 'projects__label--isSelected': selectedProjectType === p.value, 'projects__label--disabled': !showProjectTypeTiles }]">
                <img :src="p.icon" />
                <input :disabled="!showProjectTypeTiles" class="projects__radio" type="radio" :value="p.value"
                  v-model="selectedProjectType" />
                <p class="projects__text" v-html="p.label"></p>
              </label>
            </template>
          </div>
        </div>
      </fieldset>

      <fieldset :class="['fieldset', { 'opacity-20': !selectedProjectType }]">
        <!-- <fieldset :class="['fieldset', { 'opacity-20': !selectedProjectType || !address }]"> -->
        <legend class="legend">3. What are you throwing away?</legend>
        <div class="projects projects--two-column">
          <label @change="clearStates"
            :class="['projects__label', { 'projects__label--isSelected': selectedDebrisType.includes(mixedHouseholdTrash.value), 'projects__label--disabled': !selectedProjectType || mixedHouseholdTrash.canBeSelected == false }]">
            <img :src="mixedHouseholdTrash.icon" />
            <input :disabled="(!selectedProjectType || mixedHouseholdTrash.canBeSelected == false)"
              class=" projects__radio" type="checkbox" :value="mixedHouseholdTrash.value"
              v-model="selectedDebrisType" />
            <p class="projects__text" v-html="mixedHouseholdTrash.label"></p>
          </label>
          <label @change="clearStates"
            :class="['projects__label', { 'projects__label--isSelected': selectedDebrisType.includes(constructionDebris.value), 'projects__label--disabled': !selectedProjectType || constructionDebris.canBeSelected == false }]">
            <img :src="constructionDebris.icon" />
            <input :disabled="(!selectedProjectType || constructionDebris.canBeSelected == false)"
              class=" projects__radio" type="checkbox" :value="constructionDebris.value" v-model="selectedDebrisType" />
            <p class="projects__text" v-html="constructionDebris.label"></p>
          </label>
          <label @change="clearStates"
            :class="['projects__label', { 'projects__label--isSelected': selectedDebrisType.includes(yardOrOrganicMaterial.value), 'projects__label--disabled': !selectedProjectType || yardOrOrganicMaterial.canBeSelected == false }]">
            <img :src="yardOrOrganicMaterial.icon" />
            <input :disabled="(!selectedProjectType || yardOrOrganicMaterial.canBeSelected == false)"
              class=" projects__radio" type="checkbox" :value="yardOrOrganicMaterial.value"
              v-model="selectedDebrisType" />
            <p class="projects__text" v-html="yardOrOrganicMaterial.label"></p>
          </label>
          <label @change="clearStates"
            :class="['projects__label', { 'projects__label--isSelected': selectedDebrisType.includes(heavy.value), 'projects__label--disabled': !selectedProjectType || heavy.canBeSelected == false }]">
            <img :src="heavy.icon" />
            <input :disabled="(!selectedProjectType || heavy.canBeSelected == false)" class=" projects__radio"
              type="checkbox" :value="heavy.value" v-model="selectedDebrisType" />
            <p class="projects__text" v-html="heavy.label"></p>
          </label>
        </div>
      </fieldset>

      <ExpandableSelect
        v-model="selectedFillType"
        :options="fillTypes"
        :show="selectedDebrisType.includes('heavy') && selectedDebrisType.length < 2"
        title="Please select a heavy debris type (choose one):"
      />

      <dismissable :isWarning=true v-if="selectedDebrisType.includes('heavy')">
        <template v-slot:content>
          <p><strong>If your project involves more than 10 tons of debris, or if you’re disposing of multiple types of
              heavy debris, you may need to rent more than one dumpster.</strong> Fill out <a :href="quoteForm">this
              form</a> and a member of our team will reach out to discuss the best solution.</p>
        </template>
      </dismissable>

      <dismissable>
        <template v-slot:content>
          <p>Have questions about accepted and prohibited materials? <a href="#acceptedMaterials">Click here</a> to see
            what you can throw away in our dumpsters.</p>
        </template>
      </dismissable>

      <button
        :disabled="!readyForMako"
        @click="callMako"
        class="btn btn--secondary mt-2 mb-2"
        type="button"
      >
        <span>Search Available Dumpsters</span>
        <img :src="searchIcon" />
      </button>

      <DumpsterSelection id="dumpsterSelection" />
      <p class="disclaimer"><strong>Disclaimer:</strong> All dumpster dimensions may vary slightly, but their total
        volume is accurate. Prices and availability are subject to change.</p>

      <button
        :disabled="isContinueDisabled"
        @click="continueClicked"
        class="btn btn--secondary btn--orange mb-2"
        type="button"
      >
        <span class="btn__text">Continue</span>
        <img :src="iconArrowRight">
      </button>

      <div class="custom-quote mb-2 mt-1">
        <img :src="quote" alt="Need a Custom Quote?" class="custom-quote__img">
        <p v-html="customQuote"></p>
      </div>

      <!-- Fetching Mako Results Modal -->
      <modal :isOpen=isModalOpen :showClose="false">
        <template v-slot:content>
          <img class="theme-logo" :src="themeLogo" />
          <img class="spin" :src="loadingSpinner" />
          <p>Just a second! We're finding the best rates in your area.</p>
        </template>
      </modal>
    </form>
  </section>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex'
import { setupAutocomplete, timestamp, isGoogleEventsReady, getClientID, dataLayerPush, getQueryString } from '@/utilities.js'
import ExpandableSelect from '@/components/ExpandableSelect'
import Dismissable from '@/components/Dismissable.vue'
import DumpsterSelection from '@/components/DumpsterSelection.vue'
import Modal from '@/components/Modal.vue'
import config from '@/config'
const { cdn, links, customQuote, phoneNumber } = config
const { quoteForm, multipleDumpsters, commercialQuote } = links

export default {
  name: 'AddressSearch',
  components: {
    ExpandableSelect,
    Dismissable,
    DumpsterSelection,
    Modal
  },
  data: () => {
    return {
      autoCompleteIsActive: true,
      showAddressError: false,
      quoteForm,
      customQuote,
      phoneNumber,
      multipleDumpsters,
      commercialQuote,
      isModalOpen: false,
      showProjectTypeTiles: false,
      themeLogo: `${cdn}theme-logo.png`,
      iconsInfoProTip: `${cdn}icons-info-pro-tip.svg`,
      iconArrowRight: `${cdn}icons-arrow-right.svg`,
      loadingSpinner: `${cdn}icons-spinner-loading.svg`,
      searchIcon: `${cdn}icons-mag-glass-search-white.svg`,
      pinIcon: `${cdn}icons-address-marker.svg`,
      quote: `${cdn}questions-icon.svg`,
      projectTypes: [
        { value: 'homeCleanout', label: 'Home/Garage<br> Cleanout', icon: `${cdn}icons-home-cleanout.svg` },
        { value: 'homeRemodel', label: 'Home<br> Remodel', icon: `${cdn}icons-home-remodel.svg` },
        { value: 'yardCleanupOrLandscaping', label: 'Yard Cleanup/<br> Landscaping', icon: `${cdn}icons-yard-cleanup.svg` },
        { value: 'bathroomOrKitchenDemo', label: 'Bathroom/<br> Kitchen Demo', icon: `${cdn}icons-bath-kitchen-demo.svg` },
        { value: 'roofingOrSidingTearDown', label: 'Roofing/Siding<br> Tear Down', icon: `${cdn}icons-roofing-siding.svg` },
        { value: 'other', label: 'Another Type<br>of Project', icon: `${cdn}wheelbarrow-icon.svg` }
      ],
      fillTypes: [
        { value: 'concrete' },
        { value: 'dirt' },
        { value: 'brick' },
        { value: 'block' },
        { value: 'gravel' },
        { value: 'asphalt' }
      ]
    }
  },
  computed: {
    ...mapState('customer', [
      'isServiceAddressValid'
    ]),
    ...mapState('debrisTypes', [
      'constructionDebris',
      'heavy',
      'mixedHouseholdTrash',
      'types',
      'yardOrOrganicMaterial'
    ]),
    ...mapState('dumpster', [
      'selectedDumpster',
      'isMakoCallInProgress'
    ]),
    ...mapGetters('dumpster', [
      'isContinueDisabled'
    ]),
    ...mapGetters('project', [
      'readyForMako'
    ]),
    address: {
      get () {
        return this.$store.getters['customer/formattedServiceAddress']
      },
      set (value) {
        this.$store.commit('customer/serviceAddress', { postalCode: value })
      }
    },
    selectedProjectType: {
      get () {
        return this.$store.state.project.selectedProjectType
      },
      set (value) {
        this.$store.commit('project/selectedProjectType', value)
      }
    },
    selectedDebrisType: {
      get () {
        return this.$store.state.project.selectedDebrisType
      },
      set (value) {
        this.$store.commit('project/selectedDebrisType', value)
        this.$store.commit('debrisTypes/debrisMatrix', {
          debris: value,
          projectType: this.selectedProjectType
        })
      }
    },
    selectedFillType: {
      get () {
        return this.$store.getters['project/selectedFillType']
      },
      set (value) {
        this.$store.commit('project/selectedFillType', value)
      }
    }
  },
  methods: {
    ...mapMutations('customer', {
      updateIsServiceAddressValid: 'isServiceAddressValid'
    }),
    addressChange () {
      this.showProjectTypeTiles = false
      this.$store.commit('project/selectedProjectType', '')
      this.$store.commit('dumpster/makoResponse', null)
      this.$store.commit('dumpster/setSelectedDumpster', null)
      // Let google handle address validation which fires after this
      this.updateIsServiceAddressValid(false)
    },
    addressFocus () {
      this.autoCompleteIsActive = true
    },
    addressBlur () {
      this.autoCompleteIsActive = false
      // The timeout gives google autocomplete a chance to fire and set isServiceAddressValid
      // This prevents a glimpse of the invalid error message when address is input and selected from the dropdown
      setTimeout(() => {
        if (!this.isServiceAddressValid) {
          this.clearStates()
          this.showAddressError = true
        }
      }, 200)
    },
    runProjectMatrix (val) {
      this.clearStates()
      this.$store.commit('debrisTypes/projectMatrix', val)
    },
    clearStates () {
      // Reset to page load state
      this.$store.commit('dumpster/setSelectedDumpster', null)
      this.$store.commit('cart/item', null)
      this.$store.commit('customer/hasAcceptedTonnageOverages', false)
      this.$store.commit('customer/hasAcceptedRentalOverages', false)
      this.$store.commit('dumpster/makoResponse', null)
    },
    callMako () {
      if (this.readyForMako) {
        this.$store.dispatch('cart/clearSavedCart')
        this.isModalOpen = true

        const dumpsterSelection = document.getElementById('dumpsterSelection')
        dumpsterSelection.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
        return this.$store.dispatch('dumpster/callMako', {
          lat: this.$store.state.customer.serviceAddress.lat,
          lng: this.$store.state.customer.serviceAddress.lng,
          selectedFillType: this.selectedFillType,
          selectedProjectType: this.selectedProjectType,
          selectedDebrisType: this.selectedDebrisType,
          address: `${this.$store.state.customer.serviceAddress.streetAddress} ${this.$store.state.customer.serviceAddress.streetName}`,
          city: this.$store.state.customer.serviceAddress.city,
          province: this.$store.state.customer.serviceAddress.state,
          zip: this.$store.state.customer.serviceAddress.postalCode
        })
          .then(() => {
            // If UUID is present, remove it once new search is completed as the saved cart is now cleared
            if (this.$route.query?.uuid) this.$router.replace({ name: 'ChooseDumpster', query: { uuid: 'remove' } })
            this.isModalOpen = false
          })
      }
    },
    continueClicked () {
      this.$router.push('/cart/service')
    },
    googleEvents (address) {
      dataLayerPush({ event: 'ecom-address-search', serviceAddress: address })
      if (isGoogleEventsReady()) {
        const time = timestamp()
        // UA
        window.gtag('event', 'Ecommerce - AddressSearch', {
          event_category: 'Ecommerce',
          event_label: `${time}~${window.location.href}~${address.streetAddress} ${address.streetName}, ${address.city}, ${address.state} ${address.postalCode}`,
          send_to: process.env.VUE_APP_UA_ID
        })

        // GA4
        window.gtag('event', 'Ecommerce - AddressSearch', {
          event_category: 'Ecommerce',
          timestamp: time,
          address: `${address.streetAddress} ${address.streetName}, ${address.city}, ${address.state} ${address.postalCode}`,
          bd_client_id: getClientID(),
          send_to: process.env.VUE_APP_GA4_ID
        })
      }
    }
  },
  mounted () {
    // Makes sure you can selected a different project if you back to step 1
    if (this.selectedProjectType || location.search.match(/uuid/)) {
      this.showProjectTypeTiles = true
    }
    const utmSource = getQueryString('utm_source').toLowerCase()
    this.$store.commit('customer/utmSource', utmSource)

    setupAutocomplete(this.$refs.autocompleteField, (address) => {
      // If the address is valid with google
      if (address) {
        this.showAddressError = false
        this.updateIsServiceAddressValid(true)
        this.$store.commit('customer/serviceAddress', address)
        this.clearStates()
        this.showProjectTypeTiles = true
        this.googleEvents(address)
        return
      }

      // Address is invalid and errors should be displayed
      this.updateIsServiceAddressValid(false)
      this.showAddressError = true
      this.showProjectTypeTiles = false
      this.$store.commit('project/selectedProjectType', '')
      this.$store.commit('customer/serviceAddress', {})
    })
  }
}
</script>
